import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { ActivatedRoute, Router, Data } from "@angular/router";
import { HttpResponse } from "@angular/common/http";
import { BehaviorSubject, Subscription, forkJoin, Observable, of } from "rxjs";
import { mergeMap, tap, finalize, map } from "rxjs/operators";

import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { LoaderService } from "../../services/loader.service";
import { Organization } from "../../models/organization.model";
import { CasingPlanModel, WellSegmentModel } from "../../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { segmentHasData } from "../../../form-controls/components/new-casing-plan/models/casing-plan-functions.model";
import { PermitService } from "../../services/permit.service";
import { Permit, OfficialContact } from "../../models/permit.model";
import { OrganizationService } from "../../services/organization.service";
import { GISPoint, GISPointTypes } from "../../models/well.model";
import { DomainDefinitionService } from "../../../builder/services/domain-definition.service";
import { DocumentManagementService } from "../../services/document-management.service";
import { FileQueueQuery, FileQueueEntry, parseFileNameFromHeaders, OnBaseDocumentTypeProcessReference } from "../../models/onBaseIntegrationAndMapping.model";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";

@Component({
    selector: "app-permit",
    templateUrl: "./permit.component.html",
    styleUrls: ["./permit.component.scss"],
    standalone: false
})
export class PermitComponent implements OnInit, OnDestroy {

    casingPlan: CasingPlanModel;    

    @ViewChild("downloadLink") public downloadLink: ElementRef;

    errorDialog: Dialog = new Dialog("An error has occurred", [new DialogButton("OK", false, () => { return; })]);
    noDocumentDialog: Dialog = new Dialog("Unable to locate document", [new DialogButton("OK", false, () => { return; })]);

    get leaseName(): string {
        if (this.permit == null) return "";
        if (this.permit.wellName == null || this.permit.wellName === "") return "";
        if (this.permit.wellNumber == null || this.permit.wellNumber === "") return this.permit.wellName;
        if (this.permit.wellName.endsWith(this.permit.wellNumber)) {
            const name = this.permit.wellName.substring(0, this.permit.wellName.length - this.permit.wellNumber.length);
            return name;
        }
        return this.permit.wellName;
    }

    org: Organization;

    subscriptions = new Subscription();

    permit: Permit;
    permitId: string;

    wellHead: GISPoint;

    get wellHeadLatitude(): number {
        if (this.wellHead == null || this.wellHead.coordinates == null) return 0;
        if (this.wellHead.coordinates.latitude83 != null) return this.wellHead.coordinates.latitude83;
        return this.wellHead.coordinates.latitude;
    }

    get wellHeadLongitude(): number {
        if (this.wellHead == null || this.wellHead.coordinates == null) return 0;
        if (this.wellHead.coordinates.longitude83 != null) return this.wellHead.coordinates.longitude83;
        return this.wellHead.coordinates.longitude;
    }


    watersheds = [];
    wellTypes = [];
    wellTypeDescriptions = [];
    target: GISPoint;

    documentTypes: OnBaseDocumentTypeProcessReference[] = [];

    get permitDocumentTypeName(): string {
        return this.documentTypes.length > 0 ? this.documentTypes[0].onBaseDocumentTypeName : "";
    }

    get targetLatitude(): number {
        if (this.target == null || this.target.coordinates == null) return 0;
        if (this.target.coordinates.latitude != null) return this.target.coordinates.latitude;
        return this.target.coordinates.latitude83;
    }

    get targetLongitude(): number {
        if (this.target == null || this.target.coordinates == null) return 0;
        if (this.target.coordinates.longitude != null) return this.target.coordinates.longitude;
        return this.target.coordinates.longitude83;
    }

    permitLoading$ = new BehaviorSubject<boolean>(false);
    subHeader: SubHeader = new SubHeader({
        title: "Permit",
        buttons: [{ name: "View PDF", method: () => this.print() }]
    });

    get applicationData(): any {
        let result = { applicationNumber: "", wellName: "", wellNumber: "" };
        if (this.permit) {
            result.applicationNumber = this.permit.id;
            result.wellName = this.permit.wellName;
            result.wellNumber = this.permit.wellNumber;
        }

        return result;
    }

    get dayNotifyReasons(): string[] {
        return [
            "Commencement of site construction",
            "Pit excavation and closure",
            "Commencement of drilling, reopening, converting or plugback operations",
            "Installation and cementing of all casing strings",
            "BOP testing",
            "Well stimulation",
            "Plugging operations",
            "Well pad construction"
        ];
    }

    get emergencyContact(): OfficialContact {
        let emerg = this.permit.officialContacts.find(o => o.contactType === "24 Hour Emergency Contact");
        if (!emerg) {
            emerg = { contactName: "", phone: "911", contactType: "24 Hour Emergency Contact", contactTitle: "", companyName: "" } as OfficialContact;
        }
        return emerg;
    }

    get fireDepartment(): OfficialContact {
        let fire = this.permit.officialContacts.find(o => o.contactType === "Fire Department");
        if (!fire) {
            fire = { contactName: "", phone: "911", contactType: "Fire Department", contactTitle: "", companyName: "" } as OfficialContact;
        }
        return fire;
    }

    

    get immediateNotifyReasons(): string[] {
        return [
            "Discovery of defective well construction",
            "Detection of any natural gas or H2S gas during drilling in urban areas",
            "Discovery of defective well construction during well stimulation",
            "Determination that a well is a lost hole",
            "Determination that a well is a dry hole"
        ];
    }

    get medicalFacility(): OfficialContact {
        let medic = this.permit.officialContacts.find(o => o.contactType === "Closest Medical Facility");
        if (!medic) {
            medic = { contactName: "", phone: "911", contactType: "Closest Medical Facility", contactTitle: "", companyName: "" } as OfficialContact;
        }
        return medic;
    }

    private _usableStrings: WellSegmentModel[] = null;

    get usableCasingStrings(): WellSegmentModel[] {
        if (this.casingPlan == null) return [];
        if (this._usableStrings != null) return this._usableStrings;
        const explicitlyDefined = this.casingPlan.wellSegments.filter(seg => seg.hasData != null);
        const unknowns = this.casingPlan.wellSegments.filter(seg => seg.hasData == null);
        this._usableStrings = [].concat(explicitlyDefined.filter(c => c.hasData === true));
        unknowns.forEach(s => {
            const hasData = segmentHasData(s);
            if (hasData) { this._usableStrings.push(s); }
        });
        this._usableStrings = this._usableStrings.sort((a, b) => {
            let measurementA = (a.casingDiameter != null) ? a.casingDiameter : a.boreholeDiameter;
            let measurementB = (b.casingDiameter != null) ? b.casingDiameter : b.boreholeDiameter;
            if (measurementA > measurementB) return -1;
            if (measurementA < measurementB) return 1;
            return 0;
        });
        return this._usableStrings;
    }
    

    get wellType(): string {
        let type: string = "";        
        if (this.permit && this.permit.proposal && this.permit.proposal.wellType && this.wellTypes) {
            const value = this.wellTypes.find(t => t.Code === this.permit.proposal.wellType.type);
            if (value) {
                type = value.Definition;
            }
        }
        return type;
    }

    get wellTypeDescription(): string {
        let description: string = "";
        if (this.permit && this.permit.proposal && this.permit.proposal.wellType && this.wellTypeDescriptions) {
            const value = this.wellTypeDescriptions.find(t => t.Code === this.permit.proposal.wellType.description);
            if (value) {
                description = value.Definition;
            }
        }
        return description;
    }

    constructor(               
            public loader: LoaderService,
            public orgService: OrganizationService,
            public permitService: PermitService,
            public route: ActivatedRoute,
            public router: Router,
            public subHeaderService: SubHeaderService,
            public readonly domainService: DomainDefinitionService,
            private readonly _docService: DocumentManagementService,
            public dialogService: DynamicDialogService
                ) {
        
    }

    ngOnInit() {
        this.loader.toggleLoader(true);
        this.permitLoading$.next(true);
        this.subHeaderService.updateSubHeader(this.subHeader);

        this.permitId = this.route.snapshot.paramMap.get("permitId");

        const routeData$ = this.route.data.pipe(
            tap((data: Data) => {
                this.documentTypes = data.documentTypes;
            })
        ).subscribe({
            next: () => { },
            error: (err) => { console.error(err); }
        });
        this.subscriptions.add(routeData$);

        let tempTypes: [];
        const typeObs$ = this.domainService.getValues("WELL_TYPE").pipe(
            mergeMap((data) => {
                tempTypes = data;
                return this.domainService.getValues("WELL_TYP");
            }),
            map((data) => {
                return tempTypes.concat(data);
            })
         );
        const typeDescriptionObs$ = this.domainService.getValues("WLTYP_DESC");
        const waterObs$ = this.domainService.getValues("WATERSHED");
        const domains$ = forkJoin(typeObs$, typeDescriptionObs$, waterObs$ ).pipe(tap(([types, typeDescriptions, waters]) => {
            this.watersheds = waters;
            this.wellTypes = types;
            this.wellTypeDescriptions = typeDescriptions;
        }), finalize(() => { this.subscriptions.add(domains$); })).subscribe(() => { console.log("domains loaded"); }, (error) => { console.log(`Error loading domains: ${error}`); });

        const permit$ = this.permitService.getPermit(this.permitId, true).pipe(
            tap((pmt) => {
                this.permit = pmt;
                this.wellHead = this.permit.location.gisPoints.find(c => c.pointType == GISPointTypes.WellHead);
                this.target = this.permit.location.gisPoints.find(c => c.pointType == GISPointTypes.Target);
                this.casingPlan = this.permit.casingPlan as CasingPlanModel;
            }),
            mergeMap(pmt => {
                return this.orgService.getOrganization(pmt.organizationId).pipe(tap((org) => {
                    this.org = org;
                }));
            }),
            finalize(() => {
                this.permitLoading$.next(false);
                this.loader.toggleLoader(false);
                this.subscriptions.add(permit$);
            })).subscribe((acc) => { console.log("page loaded"); }, (error) => {
                console.log(error);
                this.permitLoading$.next(false);
                this.loader.toggleLoader(false);
                if (error.status === 401) {
                    this.router.navigate(["/home/unauthorized"]);
                }
            });
    }

    ngOnDestroy() {
        if (this.subscriptions) this.subscriptions.unsubscribe();
    }

    booleanToYesNo(bool: any): string {
        if (bool) {
            return "Yes";
        } else {
            return "No";
        }
    }   


    

    getWatershed(value: string): string {
        let result = "";
        if (this.watersheds) {
            const opt = this.watersheds.find(w => w.Code === value);
            if (opt) result = opt.Definition;
        }
        return result;
    }

    handleError(error: any): void {
        console.error(error);
        this.loader.toggleLoader(false);
        this.errorDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.errorDialog);
    }

    noDocument(): void {
        this.noDocumentDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.noDocumentDialog);
    }

    // Permits generated before the OnBase document type rename are still queued under this legacy name;
    // fall back to it when nothing is found under the current type so older permits can still be printed.
    private readonly legacyPermitDocumentTypeName = "DNR - OG - PRMT-Well Permit";

    private findQueuedPermitDocumentId(documentTypeName: string): Observable<string | null> {
        const query = new FileQueueQuery();
        query.fileName = `${this.permit.id}.pdf`;
        query.documentTypeName = documentTypeName;

        return this._docService.queryQueueByTypeAndName(query).pipe(map((entries: FileQueueEntry[]) => {
            let pendings = entries.filter(e => e.uploadTimeStamp == null);
            if (pendings.length > 0) return pendings[0].identifier; //assumption is that any pending items are more recent than any successfully uploaded
            let uploadeds = entries.filter(e => e.uploadTimeStamp != null);
            if (uploadeds.length > 0) {
                uploadeds = uploadeds.sort((a, b) => {
                    if (a.uploadTimeStamp > b.uploadTimeStamp) return -1;
                    if (a.uploadTimeStamp < b.uploadTimeStamp) return 1;
                    return 0;
                });
                return uploadeds[0].identifier;
            }
            return null;
        }));
    }

    print(): void {
        this.loader.toggleLoader(true);

        const permit$ = this.findQueuedPermitDocumentId(this.permitDocumentTypeName).pipe(
            mergeMap((documentId) => documentId != null ? of(documentId) : this.findQueuedPermitDocumentId(this.legacyPermitDocumentTypeName)),
            mergeMap((documentId) => documentId == null ? of(null) : this._docService.downloadFromQueue(documentId))
        ).subscribe({
            next: (data: HttpResponse<Blob> | null) => {
                this.loader.toggleLoader(false);
                if (data == null) this.noDocument();
                else {
                    const fileName = parseFileNameFromHeaders(data.headers.get("content-disposition"));
                    const blob = new Blob([data.body], { type: "application.pdf" });
					const blobUri = window.URL.createObjectURL(blob);
					const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
					anchor.href = blobUri;
					anchor.download = fileName;
					anchor.click();
                }
            },
            error: (error) => {
                this.handleError(error);
            }
        });
        this.subscriptions.add(permit$);
    }

}
